import React, { useState } from 'react';
import { Sparkles, X, Loader2, Wand2, ArrowRight, Lightbulb, PenLine } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AiDescriptionDialog - Reusable AI description generator/polisher
 *
 * Lives inside the Mekomit design system: warm cream surface, Soft Olive
 * (brand) for "generate", Warm Sand (secondary) for "polish". Calm, not loud.
 *
 * @param {Object} props
 * @param {string} props.initialText - Current description text
 * @param {'business' | 'event'} props.entityType - Type of entity
 * @param {Function} props.onApply - Callback when user applies the generated text
 * @param {Function} props.onClose - Callback to close the dialog
 * @param {Object} props.extraContext - Optional context (name, category, etc.)
 * @param {string} props.chatId - Telegram chat ID for authentication
 */
export default function AiDescriptionDialog({
    initialText = '',
    entityType = 'business',
    onApply,
    onClose,
    extraContext = {},
    chatId
}) {
    const [mode, setMode] = useState(null); // 'generate' or 'polish'
    const [previewText, setPreviewText] = useState(initialText);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [guidanceInput, setGuidanceInput] = useState(''); // User input for generation guidance
    const [showGuidanceInput, setShowGuidanceInput] = useState(false); // Show guidance input step

    const entityLabel = entityType === 'event' ? 'מפגש' : 'עסק';

    // One accent per mode — both on-palette, never a rainbow.
    const accent = mode === 'polish'
        ? {
            text: 'text-secondary-700',
            chipBg: 'bg-secondary-100',
            ring: 'focus:border-secondary-400 focus:ring-secondary-200/60',
            border: 'border-secondary-200',
            soft: 'bg-secondary-50',
            btn: 'bg-secondary-500 hover:bg-secondary-600 active:bg-secondary-700',
        }
        : {
            text: 'text-brand-700',
            chipBg: 'bg-brand-100',
            ring: 'focus:border-brand-400 focus:ring-brand-200/60',
            border: 'border-brand-200',
            soft: 'bg-brand-50',
            btn: 'bg-brand-500 hover:bg-brand-600 active:bg-brand-700',
        };

    const handleModeSelect = (selectedMode) => {
        setMode(selectedMode);
        // Show guidance input step for both modes
        setShowGuidanceInput(true);
    };

    const handleGenerateWithGuidance = () => {
        setShowGuidanceInput(false);
        handleGenerate(mode, guidanceInput);
    };

    const handleGenerate = async (selectedMode, userGuidance = '') => {
        setLoading(true);
        setError('');

        try {
            const API_ENDPOINT = process.env.REACT_APP_AI_DESCRIPTION_API ||
                'https://vukym3vho0.execute-api.us-west-2.amazonaws.com/prod/ai/description';

            // Build extra context with user guidance
            const contextWithGuidance = {
                ...extraContext,
                ...(userGuidance && { user_guidance: userGuidance })
            };

            const requestBody = {
                mode: selectedMode,
                entity_type: entityType,
                input_text: selectedMode === 'polish' ? previewText : '',
                extra_context: contextWithGuidance
            };

            const response = await fetch(API_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate description');
            }

            const data = await response.json();
            setPreviewText(data.new_text);

        } catch (err) {
            console.error('AI generation error:', err);
            setError(err.message || 'שגיאה ביצירת התיאור. נסה שוב.');
        } finally {
            setLoading(false);
        }
    };

    const handleApply = () => {
        if (previewText.trim()) {
            onApply(previewText);
            onClose();
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-modal flex items-center justify-center bg-ink/40 backdrop-blur-xs p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.96, opacity: 0, y: 8 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.96, opacity: 0, y: 8 }}
                    transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
                    className="bg-surface rounded-2xl shadow-elevated border border-line-subtle w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                    dir="rtl"
                >
                    {/* Header — calm warm gradient, never loud */}
                    <div className="relative px-8 pt-9 pb-7 text-center bg-gradient-to-b from-brand-50 via-surface to-surface">
                        <button
                            onClick={onClose}
                            className="absolute top-4 left-4 inline-flex items-center justify-center w-9 h-9 rounded-md text-ink-subtle hover:text-ink hover:bg-surface-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                            aria-label="סגור"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="mx-auto mb-4 w-14 h-14 rounded-pill bg-surface shadow-md ring-1 ring-line-subtle flex items-center justify-center">
                            <Sparkles className="w-7 h-7 text-brand-500" />
                        </div>

                        <h2 className="text-2xl font-bold text-ink-strong mb-1">
                            עוזר AI לתיאור {entityLabel}
                        </h2>
                        <p className="text-sm text-ink-muted">
                            ניסוח חם, ברור ומזמין — בלחיצת כפתור
                        </p>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
                        {/* Mode Selection (if not yet selected) */}
                        {!mode && !loading && (
                            <div className="space-y-4">
                                <p className="text-sm text-ink-muted text-center">
                                    איך תרצה שעוזר ה-AI יעזור לך?
                                </p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {/* Generate */}
                                    <button
                                        onClick={() => handleModeSelect('generate')}
                                        className="group text-right rounded-xl border border-line-subtle bg-surface-muted/60 p-5 transition-all duration-200 hover:border-brand-300 hover:bg-brand-50/60 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                    >
                                        <div className="flex flex-col items-center gap-3 text-center">
                                            <div className="w-11 h-11 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center transition-colors group-hover:bg-brand-200">
                                                <Wand2 className="w-5 h-5" />
                                            </div>
                                            <h3 className="font-semibold text-ink">
                                                צור תיאור חדש
                                            </h3>
                                            <p className="text-sm text-ink-muted leading-relaxed">
                                                ה-AI יכתוב תיאור מלא מאפס עבור ה{entityLabel}
                                            </p>
                                        </div>
                                    </button>

                                    {/* Polish */}
                                    <button
                                        onClick={() => handleModeSelect('polish')}
                                        disabled={!initialText.trim()}
                                        className="group text-right rounded-xl border border-line-subtle bg-surface-muted/60 p-5 transition-all duration-200 hover:border-secondary-300 hover:bg-secondary-50/60 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-line-subtle disabled:hover:bg-surface-muted/60 disabled:hover:shadow-none"
                                    >
                                        <div className="flex flex-col items-center gap-3 text-center">
                                            <div className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center transition-colors group-hover:bg-secondary-200">
                                                <Sparkles className="w-5 h-5" />
                                            </div>
                                            <h3 className="font-semibold text-ink">
                                                שפר תיאור קיים
                                            </h3>
                                            <p className="text-sm text-ink-muted leading-relaxed">
                                                ה-AI ילטש וינקה את התיאור שכבר כתבת
                                            </p>
                                        </div>
                                    </button>
                                </div>

                                {!initialText.trim() && (
                                    <div className="flex items-start gap-3 rounded-xl bg-secondary-50 border border-secondary-200 px-5 py-4">
                                        <Lightbulb className="w-5 h-5 text-secondary-600 shrink-0 mt-0.5" />
                                        <p className="text-sm text-ink-muted leading-relaxed">
                                            כדי לשפר תיאור קיים, כתוב תחילה טקסט בשדה התיאור.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Guidance Input Step (for both modes) */}
                        {mode && showGuidanceInput && !loading && (
                            <div className="space-y-4">
                                <div className={`rounded-xl border ${accent.border} ${accent.soft} p-5`}>
                                    <h3 className="font-semibold text-ink mb-1 flex items-center gap-2">
                                        <span className={`w-8 h-8 rounded-lg ${accent.chipBg} ${accent.text} flex items-center justify-center shrink-0`}>
                                            {mode === 'generate'
                                                ? <Wand2 className="w-4 h-4" />
                                                : <Sparkles className="w-4 h-4" />}
                                        </span>
                                        {mode === 'generate' ? `ספר לנו עוד על ה${entityLabel}` : 'מה לשפר בתיאור?'}
                                    </h3>
                                    <p className="text-sm text-ink-muted leading-relaxed">
                                        {mode === 'generate'
                                            ? 'כל מידע שתשתף יעזור ל-AI ליצור תיאור מדויק ומותאם יותר.'
                                            : 'ספר מה תרצה לשפר או לשנות בתיאור (אופציונלי).'}
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    <label className="block text-sm font-medium text-ink">
                                        {mode === 'generate'
                                            ? `מה מיוחד ב${entityLabel}? מה הוא מציע? למי זה מתאים?`
                                            : 'הנחיות לשיפור (אופציונלי)'}
                                    </label>
                                    <textarea
                                        value={guidanceInput}
                                        onChange={(e) => setGuidanceInput(e.target.value)}
                                        className={`w-full h-40 p-4 rounded-xl border border-line-strong bg-surface text-ink-strong placeholder:text-ink-subtle resize-none transition-colors focus:outline-none focus:ring-2 ${accent.ring}`}
                                        placeholder={mode === 'generate'
                                            ? `לדוגמה: ${entityType === 'business'
                                                ? 'מאפיה ביתית המתמחה בלחמי מחמצת, פועלת כבר 5 שנים, אווירה משפחתית וחמה'
                                                : 'סדנת קרמיקה למתחילים, מתאים למבוגרים וילדים מגיל 8, כולל חומרים ומרצה מנוסה'
                                            }`
                                            : 'לדוגמה: הפוך את הטון ליותר חם ואישי, הוסף דגש על הקהילה המקומית, קצר את הטקסט'}
                                        dir="rtl"
                                    />
                                    <div className="flex items-start gap-3 rounded-xl bg-secondary-50 border border-secondary-200 px-5 py-4">
                                        <Lightbulb className="w-5 h-5 text-secondary-600 shrink-0 mt-0.5" />
                                        <p className="text-sm text-ink-muted leading-relaxed">
                                            <span className="font-semibold text-ink">טיפ: </span>
                                            {mode === 'generate'
                                                ? 'ככל שתספק יותר פרטים (מה מיוחד, מה מציעים, קהל יעד, ניסיון), כך התיאור יהיה אישי ומדויק יותר. אפשר גם להשאיר ריק ולתת ל-AI לכתוב תיאור כללי.'
                                                : 'ציין מה תרצה לשפר — טון, אורך, דגשים מסוימים. אם תשאיר ריק, ה-AI ישפר באופן כללי את הבהירות, הדקדוק והמבנה.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Loading State */}
                        {loading && (
                            <div className="flex flex-col items-center justify-center py-14 space-y-4">
                                <div className="w-14 h-14 rounded-pill bg-brand-50 ring-1 ring-line-subtle flex items-center justify-center">
                                    <Loader2 className="w-7 h-7 text-brand-500 animate-spin" />
                                </div>
                                <p className="text-sm text-ink-muted animate-pulse-soft">
                                    {mode === 'generate' ? 'יוצר תיאור חדש…' : 'משפר את התיאור…'}
                                </p>
                            </div>
                        )}

                        {/* Preview Text Area */}
                        {mode && !loading && !showGuidanceInput && (
                            <div className="space-y-3">
                                <label className="flex items-center gap-2 text-sm font-medium text-ink">
                                    <PenLine className="w-4 h-4 text-ink-subtle" />
                                    תצוגה מקדימה של התיאור
                                </label>
                                <textarea
                                    value={previewText}
                                    onChange={(e) => setPreviewText(e.target.value)}
                                    className="w-full h-64 p-4 rounded-xl border border-line-strong bg-surface text-ink-strong placeholder:text-ink-subtle resize-none transition-colors focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-200/60"
                                    placeholder="התיאור יופיע כאן…"
                                    dir="rtl"
                                />
                                <p className="text-sm text-ink-subtle">
                                    ניתן לערוך את התיאור לפני השימוש
                                </p>
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div className="rounded-xl bg-danger-50 border border-danger-100 text-danger-700 px-5 py-4">
                                <p className="text-sm leading-relaxed">{error}</p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 flex justify-between items-center gap-3 border-t border-line-subtle bg-surface-muted/40">
                        <button
                            onClick={onClose}
                            className="px-5 py-2.5 text-sm font-medium text-ink-muted hover:text-ink hover:bg-surface-sunken rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        >
                            ביטול
                        </button>

                        <div className="flex gap-2">
                            {/* Back button for guidance input step */}
                            {showGuidanceInput && !loading && (
                                <button
                                    onClick={() => {
                                        setMode(null);
                                        setShowGuidanceInput(false);
                                        setGuidanceInput('');
                                    }}
                                    className="px-5 py-2.5 text-sm font-medium text-ink-muted hover:text-ink hover:bg-surface-sunken rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                >
                                    חזור
                                </button>
                            )}

                            {/* Try Again button (after generation) */}
                            {mode && !showGuidanceInput && !loading && previewText && (
                                <button
                                    onClick={() => {
                                        setMode(null);
                                        // Keep current previewText so we can iterate on it
                                        setError('');
                                        setGuidanceInput('');
                                        setShowGuidanceInput(false);
                                    }}
                                    className="px-5 py-2.5 text-sm font-medium text-ink-muted hover:text-ink hover:bg-surface-sunken rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                >
                                    נסה שוב
                                </button>
                            )}

                            {/* Generate/Polish button (in guidance input step) */}
                            {showGuidanceInput && !loading && (
                                <button
                                    onClick={handleGenerateWithGuidance}
                                    className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-ink-inverse rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:ring-brand-400 ${accent.btn}`}
                                >
                                    <Sparkles className="w-4 h-4" />
                                    {mode === 'generate' ? 'צור תיאור' : 'שפר תיאור'}
                                </button>
                            )}

                            {/* Apply button (after generation) */}
                            {mode && !showGuidanceInput && !loading && previewText && (
                                <button
                                    onClick={handleApply}
                                    disabled={!previewText.trim()}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-ink-inverse bg-brand-500 rounded-lg shadow-sm hover:bg-brand-600 hover:shadow-md active:bg-brand-700 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:ring-brand-400"
                                >
                                    השתמש בתיאור
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
